/**
 * ForgotPassword Component
 * 
 * Provides a secure password reset interface for users who have forgotten their password.
 * Features:
 * - Email validation
 * - Loading states during submission
 * - Success confirmation with instructions
 * - Links back to login and registration
 * - Support contact information
 * - Responsive design with dark mode support
 * 
 * Route: /forgot-password
 */

import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { ArrowLeft, Mail, Lock, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { authAPI, ForgotPasswordRequest, ApiError } from "../services/api";
import PageWithNavbar from "@/components/PageWithNavbar";

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const resetData: ForgotPasswordRequest = {
        email: formData.email,
      };

      const response = await authAPI.forgotPassword(resetData);
      
      if (response) {
        setIsSubmitted(true);
      } else {
        setErrors({ general: response || 'Failed to send reset email. Please try again.' });
      }
    } catch (error) {
      const errorMessage = error instanceof ApiError 
        ? error.message 
        : 'Network error. Please try again.';
      setErrors({ general: errorMessage });
      console.error('Forgot password error:', error);
    } finally {
      setIsLoading(false);
    }
    try {
      // Here you would typically send the reset request to your backend
      console.log("Password reset request for:", formData.email);
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Show success state
      setIsSubmitted(true);
    } catch (error) {
      setErrors({ general: "Failed to send password reset email. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
    // Clear general error when user modifies form
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: "" }));
    }
  };

  const handleTryDifferentEmail = () => {
    setIsSubmitted(false);
    setFormData({ email: "" });
    setErrors({});
  };

  return (
    <PageWithNavbar>
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Navigation */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <Button 
              onClick={() => navigate("/login")} 
              variant="ghost" 
              className="justify-start"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Sign In
            </Button>
            <Button 
              onClick={() => navigate("/")} 
              variant="ghost" 
              className="justify-start"
            >
              Back to Home
            </Button>
          </div>
        </div>

        <div className="max-w-md mx-auto">
          {!isSubmitted ? (
            <>
              <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                  <Lock className="h-12 w-12 text-primary mr-3" />
                  <h1 className="text-3xl font-bold">Reset Password</h1>
                </div>
                <p className="text-muted-foreground">
                  Enter your email address and we'll send you a link to reset your password
                </p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Forgot Password</CardTitle>
                  <CardDescription>
                    We'll send you a secure link to reset your password
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {errors.general && (
                      <div className="p-3 text-sm text-red-800 bg-red-100 border border-red-300 rounded-md dark:bg-red-900/20 dark:text-red-200 dark:border-red-800">
                        {errors.general}
                      </div>
                    )}

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Input
                          id="email"
                          type="email"
                          placeholder="Enter your email address"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          className={`pl-10 ${errors.email ? "border-red-500" : ""}`}
                          disabled={isLoading}
                        />
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      </div>
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        Enter the email address associated with your Doyen Trading account
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending Reset Link...
                        </>
                      ) : (
                        "Send Reset Link"
                      )}
                    </Button>
                  </form>

                  <div className="mt-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      Remember your password?{" "}
                      <Button 
                        variant="link" 
                        className="p-0 h-auto font-normal"
                        onClick={() => navigate("/login")}
                      >
                        Sign in instead
                      </Button>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                  <CheckCircle className="h-12 w-12 text-green-500 mr-3" />
                  <h1 className="text-3xl font-bold">Check Your Email</h1>
                </div>
                <p className="text-muted-foreground">
                  Password reset instructions have been sent to your email
                </p>
              </div>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center space-y-4">
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <p className="text-sm text-green-800 dark:text-green-200">
                        We've sent a password reset link to:
                      </p>
                      <p className="font-medium text-green-900 dark:text-green-100 mt-1">
                        {formData.email}
                      </p>
                    </div>

                    <div className="space-y-3 text-sm text-muted-foreground">
                      <p>Please check your email and click the reset link to continue.</p>
                      <div className="text-left bg-muted/50 p-3 rounded-md">
                        <p className="font-medium mb-2">What to do next:</p>
                        <ul className="space-y-1 text-xs">
                          <li>• Check your inbox for an email from Doyen Trading</li>
                          <li>• Click the "Reset Password" link in the email</li>
                          <li>• Follow the instructions to create a new password</li>
                          <li>• Check your spam folder if you don't see the email</li>
                        </ul>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                      <Button 
                        variant="outline" 
                        onClick={handleTryDifferentEmail}
                        className="flex-1"
                      >
                        Try Different Email
                      </Button>
                      <Button 
                        onClick={() => navigate("/login")}
                        className="flex-1"
                      >
                        Back to Sign In
                      </Button>
                    </div>

                    <div className="pt-4 border-t">
                      <p className="text-xs text-muted-foreground">
                        Didn't receive the email? Contact{" "}
                        <Button 
                          variant="link" 
                          className="p-0 h-auto text-xs"
                          onClick={() => navigate('/contact')}
                        >
                          {import.meta.env.VITE_SUPPORT_EMAIL || 'support@doyentrading.com'}
                        </Button>{" "}
                        for assistance.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
    </PageWithNavbar>
  );
};

export default ForgotPasswordPage;
